import {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import useScript, { isScriptLoaded } from '../components/Helpers/useScript';
import $window from '../tools/window';

const getAddressyFields = (addressType, address1FieldPrefix) => ([
  {
    element: `${addressType}-${address1FieldPrefix}`,
    field: '{BuildingNumber} {Street}',
    // eslint-disable-next-line no-undef, no-bitwise
    mode: pca.fieldMode.SEARCH | pca.fieldMode.POPULATE,
  },
  {
    element: `${addressType}-address-1`,
    field: '{BuildingNumber} {Street}',
    // eslint-disable-next-line no-undef
    mode: pca.fieldMode.POPULATE,
  },
  {
    element: `${addressType}-address-2`,
    field: 'SubBuilding',
    // eslint-disable-next-line no-undef
    mode: pca.fieldMode.POPULATE,
  },
  {
    element: `${addressType}-city`,
    field: 'City',
    // eslint-disable-next-line no-undef
    mode: pca.fieldMode.POPULATE,
  },
  {
    element: `${addressType}-state`,
    field: 'Province',
    // eslint-disable-next-line no-undef
    mode: pca.fieldMode.POPULATE,
  },
  {
    element: `${addressType}-postal-code`,
    field: 'PostalCode',
    // eslint-disable-next-line no-undef
    mode: pca.fieldMode.POPULATE,
  },
]);

export default function useAddressy({
  isEnabled,
  apiKey,
  onPopulateData,
  manualEntryItemLabel,
  countryCode,
  type,
}) {
  const scriptUrl = useMemo(() => 'https://api.addressy.com/js/address-3.99.js', []);

  const [, , , trigger] = useScript({
    id: 'addressy-form',
    src: scriptUrl,
    hasCacheBusting: false,
    attrs: {
      async: true,
      defer: true,
    },
    trigger: false,
  });
  const control = useRef();

  const postPopulateAddressyData = useCallback((selectedItem) => {
    onPopulateData(selectedItem);
    control.current.destroy();
    control.current = null;
  }, [onPopulateData]);

  const appendManualEntryItem = useCallback((results) => {
    const manualEntryItem = {
      Id: 'MANUALITEM',
      Type: 'СustomManualEntry',
      Text: manualEntryItemLabel?.value,
      Highlight: '0-0',
      Description: '',
      tag: manualEntryItemLabel?.value,
      title: manualEntryItemLabel?.value,
      HighlightedText: manualEntryItemLabel?.value,
      HighlightedDescription: '',
    };

    if (results[results.length - 1].Type !== 'СustomManualEntry') {
      results.push(manualEntryItem);
    }
  }, [manualEntryItemLabel]);

  const initialize = useCallback(() => {
    if (control.current || !$window?.pca?.Address) {
      return;
    }
    const fields = getAddressyFields(type, 'address-1');
    control.current = new $window.pca.Address(
      fields,
      { key: apiKey, countries: { codesList: countryCode } },
    );

    control.current.listen('populate', postPopulateAddressyData);
    control.current.listen('results', appendManualEntryItem);
  }, [apiKey, postPopulateAddressyData, appendManualEntryItem, countryCode, type]);

  const loadAddressyForm = useCallback(async () => {
    if (!isScriptLoaded(scriptUrl)) {
      trigger();
    }
    initialize();
  }, [trigger, scriptUrl, initialize]);

  useEffect(() => {
    if (!isEnabled || !apiKey) {
      return;
    }

    if (isEnabled && apiKey) {
      loadAddressyForm();
    }
  }, [isEnabled, loadAddressyForm, apiKey]);

  return {};
}
